import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import { checkoutService } from '../services/apiService';
import { formatPrice } from '../utils/currency';
import { CartSkeleton } from './Skeleton';

const PAYMENT_OPTIONS = [
  { id: 'upi', label: 'UPI' },
  { id: 'card', label: 'Card' },
  { id: 'netbanking', label: 'Net Banking' },
];

/**
 * Catalog-node marketplace checkout — single listing, escrow held until delivery.
 */
export default function MarketplaceCheckout({ catalogNodeId, listingId, vehicleId }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState('upi');
  const [placing, setPlacing] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    name: '',
    mobile: '',
    fullAddress: '',
    city: '',
    state: '',
    pincode: '',
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const addrRes = await checkoutService.getAddresses();
      setAddresses(addrRes);
      const defaultAddr = addrRes.find((a) => a.isDefault) || addrRes[0];
      const addrId = defaultAddr?._id;
      if (addrId) setSelectedAddressId((prev) => prev || addrId);

      const q = await checkoutService.getMarketplaceQuote({
        catalogNodeId,
        listingId,
        addressId: addrId,
      });
      setQuote(q);
    } catch (err) {
      toast.error(err.message || 'Could not load listing');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [catalogNodeId, listingId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!selectedAddressId) return;
    checkoutService
      .getMarketplaceQuote({ catalogNodeId, listingId, addressId: selectedAddressId })
      .then(setQuote)
      .catch(() => {});
  }, [selectedAddressId, catalogNodeId, listingId]);

  const selectedAddress = useMemo(
    () => addresses.find((a) => String(a._id) === String(selectedAddressId)),
    [addresses, selectedAddressId]
  );

  const canPlace = selectedAddressId && selectedPayment && quote;

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const addr = await checkoutService.addAddress(newAddress);
      setAddresses((prev) => [...prev, addr]);
      setSelectedAddressId(addr._id);
      setShowAddAddress(false);
      toast.success('Address saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save address');
    }
  };

  const handlePlaceOrder = async () => {
    if (!canPlace) return;
    setPlacing(true);
    try {
      const res = await checkoutService.createMarketplaceOrder({
        catalogNodeId,
        listingId,
        addressId: selectedAddressId,
        paymentMethod: selectedPayment,
        vehicleId: vehicleId || undefined,
      });

      if (res.razorpayOrderId) {
        localStorage.setItem(
          'pendingRazorpayCheckout',
          JSON.stringify({
            orderId: res.orderId,
            razorpayOrderId: res.razorpayOrderId,
            amount: res.amount,
            currency: res.currency,
          })
        );
        toast.success('Order created — proceed to payment');
        navigate('/checkout/payment', { state: { razorpayCheckout: res } });
      } else {
        toast.success('Order placed — seller has 2h to accept');
        navigate(`/orders/${res.orderId}/track`);
      }
    } catch (err) {
      if (err.code === 'OUT_OF_STOCK') {
        toast.error('This listing is no longer available.');
        navigate(`/catalog/node/${catalogNodeId}`);
      } else {
        toast.error(err.message || 'Could not place order');
      }
    } finally {
      setPlacing(false);
    }
  };

  if (loading) return <CartSkeleton />;

  if (!quote) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16 text-center">
        <p className="text-slate-600 mb-4">Listing unavailable.</p>
        <Link
          to={`/catalog/node/${catalogNodeId}`}
          className="text-indigo-600 font-semibold hover:underline"
        >
          Back to catalog node
        </Link>
      </div>
    );
  }

  const breakdown = quote.breakdown || quote;

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <Link
          to={`/catalog/node/${catalogNodeId}${vehicleId ? `?vehicleId=${vehicleId}` : ''}`}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Back to listings
        </Link>

        <h1 className="text-2xl font-bold text-slate-900">Checkout</h1>
        <p className="text-sm text-slate-500 mt-1">Payment held in escrow until delivery</p>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
            {quote.conditionLabel || quote.condition}
          </p>
          <h2 className="text-lg font-bold text-slate-900 mt-1">
            {quote.partName || quote.partCategory}
          </h2>
          {quote.subSystem && (
            <p className="text-xs text-slate-500 mt-0.5">{quote.subSystem}</p>
          )}
          <div className="mt-4 flex justify-between text-sm">
            <span className="text-slate-600">Part price</span>
            <span className="font-semibold">{formatPrice(quote.price)}</span>
          </div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-slate-600">Delivery</span>
            <span className="font-semibold">
              {quote.deliveryFee === 0 ? 'Free' : formatPrice(quote.deliveryFee)}
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between">
            <span className="font-bold text-slate-900">Total</span>
            <span className="text-xl font-bold text-orange-600">
              {formatPrice(breakdown.grandTotal ?? quote.grandTotal)}
            </span>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">
            Seller must accept within 2 hours or order is auto-cancelled with full refund.
          </p>
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2">
            <FaMapMarkerAlt /> Delivery address
          </h2>
          <div className="mt-4 grid gap-3">
            {addresses.map((addr) => (
              <button
                key={addr._id}
                type="button"
                onClick={() => setSelectedAddressId(addr._id)}
                className={`rounded-xl border p-4 text-left text-sm transition ${
                  String(selectedAddressId) === String(addr._id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="font-semibold text-slate-900">{addr.label || 'Address'}</p>
                <p className="text-slate-600 mt-1">{addr.name} · {addr.mobile}</p>
                <p className="text-slate-500 text-xs mt-1">{addr.fullAddress}, {addr.pincode}</p>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowAddAddress((v) => !v)}
            className="mt-3 text-sm font-semibold text-indigo-600 hover:underline"
          >
            {showAddAddress ? 'Cancel' : '+ Add address'}
          </button>
          {showAddAddress && (
            <form onSubmit={handleAddAddress} className="mt-4 grid gap-2 sm:grid-cols-2">
              {['name', 'mobile', 'fullAddress', 'city', 'state', 'pincode'].map((field) => (
                <input
                  key={field}
                  required
                  placeholder={field}
                  value={newAddress[field]}
                  onChange={(e) => setNewAddress((p) => ({ ...p, [field]: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm sm:col-span-2"
                />
              ))}
              <button
                type="submit"
                className="sm:col-span-2 rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white"
              >
                Save address
              </button>
            </form>
          )}
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Payment</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {PAYMENT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSelectedPayment(opt.id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  selectedPayment === opt.id
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white p-4 shadow-lg">
        <div className="mx-auto flex max-w-2xl items-center gap-4">
          <div className="flex-1">
            <p className="text-xs text-slate-500">Total payable</p>
            <p className="text-xl font-bold text-slate-900">
              {formatPrice(breakdown.grandTotal ?? quote.grandTotal)}
            </p>
          </div>
          <button
            type="button"
            disabled={!canPlace || placing}
            onClick={handlePlaceOrder}
            className="rounded-xl bg-indigo-600 px-8 py-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {placing ? 'Placing…' : 'Place order'}
          </button>
        </div>
      </div>
    </div>
  );
}
